import { api } from "@/lib/api";
import { BooLogo } from "@/components/BooLogo";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoadingIndicator, InlineError } from "@/components/QueryState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { signOut, useSession } from "@/lib/auth-client";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { ChevronRight, LogOut } from "lucide-react";
import { useEffect } from "react";

// The org picker. Two entrypoints, both full-screen (rendered outside the
// dashboard shell so there's no sidebar bound to a not-yet-chosen org):
//   • /choose-organization — where "/" sends a multi-org user with no valid
//     active org (see bounceToActiveOrg).
//   • /organization/<subpath> — links that know the destination page but not the
//     org (e.g. Brain minting /organization/billing). The choice mints the
//     canonical /$orgId/<subpath>, where the /orgs/$orgId setActive guard takes over.
// A single-org user is forwarded automatically; no reason to make them choose.

// Full-nav to the org-scoped path. The /$orgId route re-runs setActive on load,
// so we don't switch active org here — just hand off the resolved URL. Preserve
// any query string on the original sentinel link.
function goToOrg(orgId: string, subpath: string) {
  const clean = subpath.replace(/^\/+/, "");
  const search = typeof window !== "undefined" ? window.location.search : "";
  window.location.assign(`/orgs/${orgId}/${clean}${search}`);
}

export function OrgPickerRedirect() {
  useDocumentTitle("Choose an organization");
  // For the `/organization/<page>` sentinels the destination is the pathname minus
  // the sentinel prefix. Every other entry (e.g. /choose-organization) is the bare
  // dashboard home, so there's no subpath — the choice lands on /orgs/$orgId.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const subpath = pathname.startsWith("/organization")
    ? pathname.replace(/^\/organization\/?/, "")
    : "";

  const orgsQuery = useQuery({ queryKey: ["orgs"], queryFn: api.orgs.list });
  const orgs = orgsQuery.data;

  // Who's signed in — Madhur asked the picker to name the account so you're never
  // guessing which login you're on before you pick a workspace (the "/" landed me
  // in a foreign org" confusion). Session is already cached app-wide; this read is
  // free.
  const { data: session } = useSession();
  const email = session?.user?.email;

  // Auto-forward the unambiguous cases (0 → onboarding, 1 → straight through).
  useEffect(() => {
    if (!orgs) return;
    if (orgs.length === 0) {
      window.location.assign("/onboarding");
      return;
    }
    if (orgs.length === 1) {
      goToOrg(orgs[0].id, subpath);
    }
  }, [orgs, subpath]);

  if (orgsQuery.isError) {
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <InlineError error={orgsQuery.error ?? "Failed to load organizations"} />
      </div>
    );
  }

  // Loading, or auto-forwarding a 0/1-org user — show a spinner, not a flash of
  // the picker.
  if (!orgs || orgs.length <= 1) {
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6">
      <div className="flex items-center gap-2">
        <BooLogo className="size-7 shrink-0" />
        <span className="text-lg font-semibold tracking-tight">Boo</span>
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Choose an organization</CardTitle>
          <CardDescription>
            {email ? (
              <>
                Signed in as <span className="font-medium text-foreground">{email}</span>. Pick which
                workspace to open.
              </>
            ) : (
              "Pick which workspace to open."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {orgs.map((org) => (
            <Button
              key={org.id}
              variant="outline"
              className="h-11 justify-between"
              onClick={() => goToOrg(org.id, subpath)}
            >
              <span className="truncate">{org.name}</span>
              <ChevronRight className="size-4 opacity-50" />
            </Button>
          ))}
        </CardContent>
        <CardFooter>
          {/* Escape hatch: wrong account? Sign out straight from the picker
              rather than stranding the viewer among orgs that aren't theirs. */}
          <Button
            variant="ghost"
            className="text-muted-foreground w-full justify-center"
            onClick={() =>
              signOut({ fetchOptions: { onSuccess: () => window.location.assign("/login") } })
            }
          >
            <LogOut className="size-4" />
            Sign out
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
